import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { payrollCutoffService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';
import { semiMonthlyCutoff, localDateKey, type CutoffPeriod } from '@/utils/format';

/** One period from the tenant's calendar, carrying the server's own stable `key`. */
export interface CutoffCalendarPeriod extends CutoffPeriod {
  /** The server's period key — the identity a UI selection is held by. */
  key: string;
  isCurrent: boolean;
}

/**
 * The tenant's REAL payroll cut-off calendar, as configured in ERPat web →
 * HR Settings → Payroll Cut-off.
 *
 * WHY. `utils/format.ts semiMonthlyCutoff()` derives the 1st–15th / 16th–EOM split on the
 * device. It is honest about being a derivation, and it was the only option when it was written.
 * But a tenant can run any calendar — one on this build pays 11–25 and 26–10 — so the derived
 * dates were simply not that company's pay periods, and "Current Cut Off" showed a window
 * payroll never uses. The server now answers the real boundaries for the calling employee
 * (a department can be assigned its own calendar), computed from the same rule the payroll is
 * built from.
 *
 * The app does NO cut-off arithmetic against this: every boundary here came from the server.
 * A second implementation would eventually disagree with the payroll built from the same rule.
 *
 * A tenant with no calendar configured is a normal state, not a failure — `isAvailable` goes
 * false and callers fall back to the device-derived period, exactly as before.
 */
/** How many cut-offs back the DERIVED ladder reaches — matches the attendance picker's history. */
const DERIVED_LADDER_BACK = 12;

export const useCutoffStore = defineStore('cutoff', () => {
  const current = ref<CutoffPeriod | null>(null);
  const previous = ref<CutoffPeriod | null>(null);
  /**
   * The whole published calendar, newest first — what lets a reviewer reach a
   * cut-off OLDER than "previous". The endpoint already returns it; keeping only
   * current/previous threw the history away and capped the page at one period back.
   */
  const periods = ref<CutoffCalendarPeriod[]>([]);
  const loading = ref(false);
  const loaded = ref(false);

  /** True only when the tenant has a configured calendar the server could resolve. */
  const isAvailable = computed<boolean>(() => current.value !== null);

  /**
   * "Aug 26 – Sep 10" — the compact, year-less style the cut-off chips already use.
   *
   * Built from the server's DATES rather than reusing its longer label ("Aug 26 - Sep 10, 2026"),
   * so the chip keeps its existing width and the UI is unchanged. The dates are parsed as LOCAL
   * midday, not `new Date('YYYY-MM-DD')` — that parses as UTC and renders as the previous day
   * for anyone west of Greenwich, which would silently shift every label by one.
   */
  function compactLabel(startKey: string, endKey: string): string {
    const at = (key: string): Date => {
      const [y, m, d] = key.split('-').map(Number);
      return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12);
    };
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    return `${fmt(at(startKey))} – ${fmt(at(endKey))}`;
  }

  /** The fetch currently open — every caller that arrives while it runs awaits this one. */
  let inFlight: Promise<void> | null = null;

  /**
   * Fetch the calendar. Idempotent by default so a kept-alive page re-entering does not refetch;
   * pass `force` to re-read after a period rolls over.
   *
   * SINGLE-FLIGHT. A caller arriving mid-fetch JOINS it rather than returning straight away:
   * Home's pay-period cards, Reports and the Timesheet can all ask at once, and an early return
   * would let the late caller read an empty calendar and silently measure the derived dates.
   */
  function load(force = false): Promise<void> {
    if (inFlight) return inFlight;
    if (loaded.value && !force) return Promise.resolve();

    const request = fetchCalendar();
    inFlight = request;
    void request.finally(() => {
      if (inFlight === request) inFlight = null;
    });
    return request;
  }

  async function fetchCalendar(): Promise<void> {
    // A calendar that lands after sign-out belongs to the previous account — see the
    // session-epoch guard in `services/api/erpat-client.ts`.
    const epoch = getSessionEpoch();
    loading.value = true;
    try {
      const calendar = await payrollCutoffService.mine();
      if (epoch !== getSessionEpoch()) return;
      const toPeriod = (p: { start_date: string; end_date: string } | null): CutoffPeriod | null =>
        p ? { startKey: p.start_date, endKey: p.end_date, label: compactLabel(p.start_date, p.end_date) } : null;

      current.value = toPeriod(calendar.current);
      previous.value = toPeriod(calendar.previous);
      // Sorted here, once, rather than trusting the server's order: the picker
      // reads "newest first" top to bottom, and `start_date` is a zero-padded
      // `YYYY-MM-DD`, so a plain string compare is a correct date sort.
      periods.value = calendar.periods
        .map((p) => ({
          key: p.key,
          startKey: p.start_date,
          endKey: p.end_date,
          label: compactLabel(p.start_date, p.end_date),
          isCurrent: p.is_current === true,
        }))
        .sort((a, b) => (a.startKey < b.startKey ? 1 : a.startKey > b.startKey ? -1 : 0));
      loaded.value = true;
    } catch {
      if (epoch !== getSessionEpoch()) return;
      // The cut-off chips are an enhancement: on failure the caller keeps the device-derived
      // period and the screen still works.
      //
      // `loaded` is deliberately left FALSE. Latching it here would mean one blip in a lift or on
      // a train permanently pinned the app to the derived dates for the rest of the session,
      // because load() short-circuits on `loaded` and nothing else could force a retry.
      //
      // It has to be CLEARED, not merely left alone: a forced reload (pull-to-refresh) that fails
      // after an earlier success arrives here with `loaded` already true, and skipping this would
      // drop the calendar below while keeping the flag that stops it ever being re-read.
      loaded.value = false;
      current.value = null;
      previous.value = null;
      periods.value = [];
    } finally {
      if (epoch === getSessionEpoch()) loading.value = false;
    }
  }

  /**
   * {@link load}, plus ONE re-read when the cached live period has already ended: the app stayed
   * open across a rollover, and the cached `is_current` flag went stale with the calendar. The
   * derived ladder is recomputed from today on every call, so it never needs this.
   */
  async function loadCurrent(): Promise<void> {
    await load();
    const snapshot = ladder();
    const live = snapshot.periods[snapshot.currentIndex];
    if (!snapshot.derived && live && live.endKey < localDateKey(new Date())) await load(true);
  }

  /**
   * The cut-off ladder the Timesheet browser steps along, OLDEST FIRST, plus the
   * index of the period holding today.
   *
   * SERVER TRUTH AND DERIVED DATES ARE NEVER MIXED, the same rule the attendance
   * cut-off picker follows: the ladder is either wholly the tenant's published
   * calendar or wholly the device-derived semi-monthly ladder, never a blend of
   * dates computed from two different rules. `derived` says which, so the UI can
   * label a guess as a guess.
   *
   * How far it reaches FORWARD differs by source, and that is the point. The
   * derived ladder runs one period ahead, because the arithmetic is the same in
   * either direction. The published one stops wherever payroll has published to:
   * inventing a next period would be exactly the mixing this rule forbids, and
   * "the next cut-off isn't posted yet" is the honest answer.
   *
   * Call {@link load} first — an unloaded store reports the derived ladder.
   */
  function ladder(): {
    periods: { key: string; startKey: string; endKey: string }[];
    derived: boolean;
    currentIndex: number;
  } {
    const todayKey = localDateKey(new Date());

    if (periods.value.length > 0) {
      // Held newest-first for the picker; the browser reads oldest → newest.
      const ordered = [...periods.value].sort((a, b) => a.startKey.localeCompare(b.startKey));

      // The server's own `is_current` is the authority. Its fallbacks matter for
      // a calendar with a gap: prefer the period holding today, else the newest
      // that has already started, else the oldest published.
      let index = ordered.findIndex((p) => p.isCurrent);
      if (index < 0) index = ordered.findIndex((p) => p.startKey <= todayKey && p.endKey >= todayKey);
      if (index < 0) {
        for (let i = 0; i < ordered.length; i += 1) {
          if (ordered[i]!.startKey <= todayKey) index = i;
        }
      }

      return {
        periods: ordered.map((p) => ({ key: p.key, startKey: p.startKey, endKey: p.endKey })),
        derived: false,
        currentIndex: index < 0 ? 0 : index,
      };
    }

    // No calendar configured — extend the same derivation every other cut-off
    // surface falls back to, oldest first, one period past the live one.
    const offsets: number[] = [];
    for (let i = DERIVED_LADDER_BACK; i >= -1; i -= 1) offsets.push(-i);
    const fallback = offsets.map((offset) => {
      const { startKey, endKey } = semiMonthlyCutoff(offset);
      return { key: `${startKey}_${endKey}`, startKey, endKey };
    });

    // Offset 0 sits at the end of the backward run, before the single forward one.
    return { periods: fallback, derived: true, currentIndex: DERIVED_LADDER_BACK };
  }

  /**
   * Forget the calendar. MUST be called on sign-out.
   *
   * It is scoped to BOTH the tenant and the employee (a department can have its own calendar),
   * and signing into another company on one device is a designed flow — the sign-in screen asks
   * for the company key every time. Sign-in is a router.replace with no page reload, so Pinia
   * survives it: without this, the next person would inherit the previous one's pay periods and
   * their attendance would be filtered by them.
   */
  function reset(): void {
    current.value = null;
    previous.value = null;
    periods.value = [];
    loaded.value = false;
    loading.value = false;
    inFlight = null;
  }

  return { current, previous, periods, loading, loaded, isAvailable, load, loadCurrent, ladder, reset };
});
